// File: src/components/admin/scheduler/SchedulerUsersTable.js
// Paginated table of users with active schedules

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Chip,
    Typography,
    TextField,
    InputAdornment,
    Skeleton,
    Link,
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Pause as PauseIcon,
    PlayArrow as ResumeIcon,
    Search as SearchIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { formatRelativeTime, formatFutureTime } from '../../../utils/scheduleUtils';

const SchedulerUsersTable = ({
    users,
    loading,
    pagination,
    searchTerm,
    onSearchChange,
    onPageChange,
    onRowsPerPageChange,
    onViewUser,
    onPauseUser,
    onResumeUser,
}) => {
    // Filter users by search term
    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            user.uid?.toLowerCase().includes(term) ||
            user.email?.toLowerCase().includes(term)
        );
    });

    return (
        <Paper sx={{ mb: 3 }} data-testid="scheduler-users-table">
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                    Users with Schedules
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search by email or UID..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 250 }}
                />
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell align="center">Schedules</TableCell>
                            <TableCell align="center">Enabled</TableCell>
                            <TableCell align="center">Failed</TableCell>
                            <TableCell>Health</TableCell>
                            <TableCell>Last Execution</TableCell>
                            <TableCell>Next Run</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            // Loading skeleton
                            Array.from({ length: 5 }).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                </TableRow>
                            ))
                        ) : filteredUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {searchTerm ? 'No users match your search' : 'No users with schedules found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredUsers.map((user) => {
                                const hasFailures = (user.failedCount || 0) > 0;
                                const allPaused = user.enabledCount === 0 && user.totalCount > 0;

                                return (
                                    <TableRow
                                        key={user.uid}
                                        data-testid={`scheduler-user-row-${user.uid}`}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: hasFailures ? 'error.light' : 'inherit',
                                            '&:hover': {
                                                bgcolor: hasFailures ? 'error.100' : 'action.hover',
                                            },
                                        }}
                                        onClick={() => onViewUser(user.uid)}
                                    >
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Box>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/users/${user.uid}`}
                                                    underline="hover"
                                                    variant="body2"
                                                    fontWeight={500}
                                                >
                                                    {user.email || user.uid}
                                                </Link>
                                                {user.email && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {user.uid}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={user.totalCount || 0}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={user.enabledCount || 0}
                                                size="small"
                                                color={allPaused ? 'default' : 'success'}
                                                variant={allPaused ? 'outlined' : 'filled'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasFailures ? (
                                                <Chip
                                                    icon={<WarningIcon />}
                                                    label={user.failedCount}
                                                    size="small"
                                                    color="error"
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">0</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const enabledCount = user.enabledCount || 0;
                                                const nextRun = user.nextRun;
                                                if (enabledCount === 0) {
                                                    return <Chip label="idle" size="small" variant="outlined" />;
                                                }
                                                if (!nextRun) {
                                                    // No nextRun could mean event-only schedules (healthy) or a missing alarm
                                                    return <Chip label="no alarm" size="small" color="default" variant="outlined" />;
                                                }
                                                if (nextRun < Date.now() - 10 * 60 * 1000) {
                                                    return <Chip icon={<WarningIcon />} label="overdue" size="small" color="error" />;
                                                }
                                                return <Chip label="ok" size="small" color="success" />;
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatRelativeTime(user.lastExecution)}
                                            </Typography>
                                            {user.lastExecutionStatus && (
                                                <Typography
                                                    variant="caption"
                                                    color={user.lastExecutionStatus === 'COMPLETED' ? 'success.main' : 'error.main'}
                                                >
                                                    {user.lastExecutionStatus}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatFutureTime(user.nextRun)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onViewUser(user.uid)}
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {allPaused ? (
                                                <Tooltip title="Resume All">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => onResumeUser(user.uid)}
                                                    >
                                                        <ResumeIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Pause All">
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() => onPauseUser(user.uid)}
                                                    >
                                                        <PauseIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={pagination?.total || 0}
                page={(pagination?.page || 1) - 1}
                rowsPerPage={pagination?.pageSize || 20}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(e, newPage) => onPageChange(newPage + 1)}
                onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            />
        </Paper>
    );
};

export default SchedulerUsersTable;
